import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Typography, Table, Upload, Empty, Pagination, Input, Modal, Space, message } from "antd";
import { InboxOutlined, CloseCircleFilled } from "@ant-design/icons";
import styled from "styled-components";

import { useApi, ApiNames } from "../../hooks/api";
import { selectIsPhiAllowed } from "../../store/selector";
import { WHITE, GRAY_25, GRAY_300, GRAY_500, GRAY_700, PRIMARY_600 } from "../../constant/colors";
import { TableWrap, ActionButton, SecondaryActionButton } from "./taskStyles";
import { patientLabel, formatRelativeTime } from "./taskHelpers";
import { useAutoRefetch } from "./useAutoRefetch";
import { SectionError } from "./SectionError";

const { Text } = Typography;
const { Dragger } = Upload;

const PAGE_SIZE = 5;

// Image MIME types the screenshot uploader accepts. Drives both the file-dialog
// `accept` hint and the staging guard — drag-drop and programmatic adds bypass
// `accept`, so the guard is the real enforcement point.
const ACCEPTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg"];

const UploadArea = styled.div`
  padding: 16px;
`;

const StagedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
  margin-top: 14px;
  cursor: default;
`;

const ThumbnailCard = styled.div`
  position: relative;
  background: ${WHITE};
  border: 1px solid ${GRAY_300};
  border-radius: 8px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 100px;
  object-fit: contain;
  background: ${GRAY_25};
  border-radius: 4px;
`;

const ThumbnailName = styled(Text)`
  font-size: 11px;
  color: ${GRAY_700};
  width: 100%;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  background: ${WHITE};
  border: none;
  border-radius: 50%;
  padding: 0;
  line-height: 0;
  cursor: pointer;
  font-size: 18px;
  color: ${GRAY_500};
  transition: color 120ms ease-in-out;
  &:hover {
    color: ${PRIMARY_600};
  }
`;

const StagingActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
`;

/**
 * Tasks hub — EMR screenshots section. UX flow:
 *
 *   1. Operator ticks checkboxes on patients in the table (selections
 *      persist across pages).
 *   2. Operator drops/picks screenshots in the staging area.
 *   3. Operator clicks Submit → confirmation dialog → one POST.
 *
 * The table is server-side paginated (5/page). A patient ID filter
 * input narrows results server-side; selections made before filtering
 * are preserved in `selectedTaskIds`.
 */
const ScreenshotTasksSection = ({ onCountChange, refreshNonce }) => {
  const { t } = useTranslation();

  const [fetchTasks, tasksResult, tasksLoading, tasksError] = useApi(ApiNames.AgenticEmrTasksList);
  const [uploadScreenshots, , uploading] = useApi(ApiNames.AgenticScreenshotsUpload);
  const isPhiAllowed = useSelector(selectIsPhiAllowed);

  // ── Server-side pagination + filter ──────────────────────────────
  const [tablePage, setTablePage] = useState(1);
  const [tableFilter, setTableFilter] = useState("");

  // ── Staging ───────────────────────────────────────────────────────
  const [stagedItems, setStagedItems] = useState([]);

  // ── Row selection (persists across pages) ─────────────────────────
  const [selectedTaskIds, setSelectedTaskIds] = useState(() => new Set());
  // taskId → row, accumulated as the operator selects across pages/filters.
  // Lets the confirm dialog name every selected patient even when they were
  // ticked on a page that's no longer rendered.
  const [selectedRowsById, setSelectedRowsById] = useState(() => new Map());

  // ── Optimistic hide after submit ──────────────────────────────────
  const [optimisticDoneIds, setOptimisticDoneIds] = useState(() => new Set());

  // ── Stable refs ───────────────────────────────────────────────────
  const fetchTasksRef = useRef(fetchTasks);
  fetchTasksRef.current = fetchTasks;
  const tablePageRef = useRef(tablePage);
  tablePageRef.current = tablePage;
  const tableFilterRef = useRef(tableFilter);
  tableFilterRef.current = tableFilter;

  const refetch = useCallback(() => {
    const params = { limit: PAGE_SIZE, offset: (tablePageRef.current - 1) * PAGE_SIZE };
    const pid = parseInt(tableFilterRef.current, 10);
    if (!isNaN(pid) && pid > 0) params.patientId = pid;
    fetchTasksRef.current(params);
  }, []);

  // Initial load + auto-refresh, paused while an upload is in flight so a poll
  // can't swap the rows mid-submit.
  useAutoRefetch(refetch, uploading);

  // Re-fetch on page / filter change (skip initial mount)
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    refetch();
  }, [tablePage, tableFilter, refetch]);

  // Manual refresh from hub header
  useEffect(() => {
    if (refreshNonce > 0) refetch();
  }, [refreshNonce, refetch]);

  // Clear optimistic set when server confirms new data
  useEffect(() => {
    if (tasksResult) setOptimisticDoneIds(new Set());
  }, [tasksResult]);

  // Cleanup object URLs on unmount
  const stagedItemsRef = useRef(stagedItems);
  stagedItemsRef.current = stagedItems;
  useEffect(() => {
    return () => {
      stagedItemsRef.current.forEach((item) => {
        try {
          URL.revokeObjectURL(item.previewUrl);
        } catch (e) {
          /* no-op */
        }
      });
    };
  }, []);

  const pendingRows = useMemo(
    () => (tasksResult?.rows ?? []).filter((r) => !optimisticDoneIds.has(r.taskId)),
    [tasksResult, optimisticDoneIds]
  );

  useEffect(() => {
    if (tasksResult) onCountChange(tasksResult.total ?? 0);
  }, [tasksResult, onCountChange]);

  // ── Staging handlers ──────────────────────────────────────────────
  const handleStageFiles = (file, fileList) => {
    if (file === fileList[fileList.length - 1]) {
      const images = fileList.filter((f) => ACCEPTED_IMAGE_TYPES.includes(f.type));
      if (images.length < fileList.length) {
        message.warning(
          t(
            "agentic_platform.emr_tasks.upload_non_image",
            "Only PNG or JPEG images can be uploaded — other files were skipped."
          )
        );
      }
      if (images.length) {
        const newItems = images.map((f) => ({ file: f, previewUrl: URL.createObjectURL(f) }));
        setStagedItems((prev) => [...prev, ...newItems]);
      }
    }
    return Upload.LIST_IGNORE;
  };

  const removeStagedItem = (index) => {
    setStagedItems((prev) => {
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      if (removed) {
        try {
          URL.revokeObjectURL(removed.previewUrl);
        } catch (e) {
          /* no-op */
        }
      }
      return next;
    });
  };

  const clearStaging = () => {
    stagedItems.forEach((item) => {
      try {
        URL.revokeObjectURL(item.previewUrl);
      } catch (e) {
        /* no-op */
      }
    });
    setStagedItems([]);
  };

  // ── Submit ────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const taskIds = Array.from(selectedTaskIds);
    if (stagedItems.length === 0 || taskIds.length === 0) return;

    const formData = new FormData();
    stagedItems.forEach((item) => formData.append("screenshots", item.file));

    try {
      const result = await uploadScreenshots({ formData, taskIds });
      const uploaded = result?.uploaded?.length ?? 0;
      const failed = result?.errors?.length ?? 0;
      const batchCommitted = uploaded > 0 && failed === 0;
      const firstReason = result?.errors?.[0]?.reason ?? null;

      if (batchCommitted) {
        message.success(
          t("agentic_platform.emr_tasks.upload_all_ok", "Submitted {{count}} screenshot(s) for {{taskCount}} task(s)", {
            count: uploaded,
            taskCount: taskIds.length
          })
        );
        setOptimisticDoneIds((prev) => {
          const next = new Set(prev);
          taskIds.forEach((id) => next.add(id));
          return next;
        });
        setSelectedTaskIds(new Set());
        setSelectedRowsById(new Map());
        clearStaging();
      } else if (uploaded > 0 && failed > 0) {
        message.warning(
          t(
            "agentic_platform.emr_tasks.upload_partial",
            "{{ok}} screenshot(s) submitted, {{fail}} couldn't be uploaded ({{reason}}). Tasks are still pending — please try again.",
            { ok: uploaded, fail: failed, reason: firstReason ?? "unknown reason" }
          )
        );
      } else {
        message.error(
          t(
            "agentic_platform.emr_tasks.upload_all_failed",
            "Couldn't upload your screenshots ({{reason}}). Please try again.",
            {
              reason: firstReason ?? "no details"
            }
          )
        );
      }
      refetch();
    } catch (err) {
      message.error(
        t("agentic_platform.emr_tasks.upload_failed", "Couldn't upload your screenshots. Please try again.")
      );
    }
  };

  const confirmAndSubmit = () => {
    const taskIds = Array.from(selectedTaskIds);
    // Resolve the selected ids to patient labels (works for off-page picks via
    // the accumulated map). Fall back to the id if a row was never seen.
    const selectedPatients = taskIds.map((id) => {
      const row = selectedRowsById.get(id);
      return row ? patientLabel(row) : `#${id}`;
    });
    Modal.confirm({
      title: t("agentic_platform.emr_tasks.submit_title", "Submit screenshots"),
      content: (
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Text>
            {t(
              "agentic_platform.emr_tasks.submit_confirm_body",
              "Submit {{screenshots}} screenshot(s) for the following {{patients}} patient(s)?",
              { screenshots: stagedItems.length, patients: taskIds.length }
            )}
          </Text>
          <ul style={{ margin: 0, paddingLeft: 18, maxHeight: 180, overflowY: "auto" }}>
            {selectedPatients.map((label, i) => (
              <li key={taskIds[i]}>
                <Text style={{ fontSize: 13 }}>{label}</Text>
              </li>
            ))}
          </ul>
        </Space>
      ),
      okText: t("agentic_platform.emr_tasks.submit_confirm", "Confirm"),
      cancelText: t("agentic_platform.emr_tasks.submit_cancel", "Cancel"),
      onOk: handleSubmit
    });
  };

  const columns = [
    {
      title: t("agentic_platform.emr_tasks.col_patient", "Patient"),
      key: "patient",
      render: (_v, row) => (
        <a className="who-link" href={`/patient/${row.patientId}`} target="_blank" rel="noreferrer">
          {patientLabel(row)}
        </a>
      )
    },
    ...(isPhiAllowed
      ? [
          {
            title: t("agentic_platform.emr_tasks.col_mrn", "MRN"),
            key: "mrn",
            render: (_v, row) =>
              row.mrn ? (
                <Text
                  className="muted"
                  style={{ fontFamily: "monospace", fontSize: 12 }}
                  copyable={{
                    text: row.mrn,
                    tooltips: [
                      t("agentic_platform.emr_tasks.copy_mrn", "Copy MRN"),
                      t("agentic_platform.emr_tasks.copied_mrn", "Copied")
                    ]
                  }}
                >
                  {row.mrn}
                </Text>
              ) : (
                <span className="muted">—</span>
              )
          }
        ]
      : []),
    {
      title: t("agentic_platform.emr_tasks.col_created", "Pending since"),
      key: "createdAt",
      width: 160,
      render: (_v, row) => <span className="muted">{formatRelativeTime(row.createdAt)}</span>
    }
  ];

  const rowSelection = {
    selectedRowKeys: Array.from(selectedTaskIds),
    onChange: (keys, rows) => {
      const keySet = new Set(keys);
      setSelectedTaskIds(keySet);
      // Maintain the id→row map so off-page selections can still be named at
      // confirm time. Merge in the currently-visible selected rows, then drop
      // any entries no longer selected.
      setSelectedRowsById((prev) => {
        const next = new Map(prev);
        rows.forEach((r) => {
          if (r) next.set(r.taskId, r);
        });
        Array.from(next.keys()).forEach((id) => {
          if (!keySet.has(id)) next.delete(id);
        });
        return next;
      });
    },
    // Keep selections from other pages intact; only toggle keys visible now.
    preserveSelectedRowKeys: true
  };

  const hasStaged = stagedItems.length > 0;
  const hasSelection = selectedTaskIds.size > 0;
  const serverTotal = tasksResult?.total ?? 0;

  return (
    <>
      <UploadArea>
        <Dragger
          name="screenshots"
          accept={ACCEPTED_IMAGE_TYPES.join(",")}
          multiple
          showUploadList={false}
          beforeUpload={handleStageFiles}
          disabled={uploading}
          data-testid="agentic-screenshot-dragger"
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined style={{ color: PRIMARY_600 }} />
          </p>
          <p className="ant-upload-text">
            {t("agentic_platform.emr_tasks.upload_hint", "Drag screenshots here, or click to browse")}
          </p>
          <p className="ant-upload-hint" style={{ color: GRAY_500 }}>
            {t(
              "agentic_platform.emr_tasks.upload_subhint",
              "Select patients in the table below, drop screenshots here, then Submit."
            )}
          </p>
          {hasStaged && (
            <StagedGrid onClick={(e) => e.stopPropagation()}>
              {stagedItems.map((item, i) => (
                <ThumbnailCard key={`${item.file.name}-${i}`}>
                  <RemoveButton
                    aria-label={t("agentic_platform.emr_tasks.remove_staged", "Remove")}
                    title={t("agentic_platform.emr_tasks.remove_staged", "Remove")}
                    onClick={() => removeStagedItem(i)}
                    disabled={uploading}
                  >
                    <CloseCircleFilled />
                  </RemoveButton>
                  <Thumbnail src={item.previewUrl} alt={item.file.name} />
                  <ThumbnailName title={item.file.name}>{item.file.name}</ThumbnailName>
                </ThumbnailCard>
              ))}
            </StagedGrid>
          )}
        </Dragger>

        {(hasStaged || hasSelection) && (
          <StagingActions>
            {hasSelection && (
              <Text type="secondary" style={{ fontSize: 12, marginRight: "auto" }}>
                {t("agentic_platform.emr_tasks.selected_count", "{{count}} patient(s) selected", {
                  count: selectedTaskIds.size
                })}
              </Text>
            )}
            {hasStaged && (
              <SecondaryActionButton onClick={clearStaging} disabled={uploading}>
                {t("agentic_platform.emr_tasks.clear_staged", "Clear")}
              </SecondaryActionButton>
            )}
            <ActionButton onClick={confirmAndSubmit} loading={uploading} disabled={!hasStaged || !hasSelection}>
              {t("agentic_platform.emr_tasks.submit_button", "Submit {{count}} screenshot(s)", {
                count: stagedItems.length
              })}
            </ActionButton>
          </StagingActions>
        )}
      </UploadArea>

      {/* Patient ID filter */}
      <div style={{ padding: "8px 16px 4px" }}>
        <Input
          placeholder={t("agentic_platform.emr_tasks.filter_placeholder", "Filter by patient ID")}
          value={tableFilter}
          onChange={(e) => {
            setTableFilter(e.target.value);
            setTablePage(1);
          }}
          allowClear
          style={{ maxWidth: 220 }}
        />
      </div>

      {tasksError && !tasksLoading ? (
        <SectionError onRetry={refetch} testId="agentic-tasks-error-screenshots" />
      ) : !tasksLoading && pendingRows.length === 0 ? (
        <div style={{ padding: 24 }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              tableFilter
                ? t("agentic_platform.emr_tasks.empty_filtered", "No pending tasks match patient ID {{id}}.", {
                    id: tableFilter
                  })
                : t(
                    "agentic_platform.emr_tasks.empty",
                    "No pending tasks. New tasks appear here when the agent needs EMR data for a patient."
                  )
            }
          />
        </div>
      ) : (
        <TableWrap>
          <Table
            size="small"
            rowKey="taskId"
            columns={columns}
            dataSource={pendingRows}
            loading={tasksLoading}
            rowSelection={rowSelection}
            pagination={false}
          />
          {serverTotal > PAGE_SIZE && (
            <div style={{ padding: "12px 16px", textAlign: "right" }}>
              <Pagination
                current={tablePage}
                pageSize={PAGE_SIZE}
                total={serverTotal}
                onChange={(page) => setTablePage(page)}
                showSizeChanger={false}
                size="small"
              />
            </div>
          )}
        </TableWrap>
      )}
    </>
  );
};

export { ScreenshotTasksSection };
